import React, {ReactNode} from 'react'
import styled from '@emotion/styled'

import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import {Text, TextVariant} from '../Text'

interface Props {
  /** Pass through className to allow styles overrides */
  className?: string
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Whether the radio is checked or not */
  checked: boolean
  /** Whether the radio is disabled or not */
  disabled?: boolean
  /** Whether the radio container should render full width */
  fullWidth?: boolean
  /** Whether the input has an error */
  hasError?: boolean
  /** Content to render in label next to the radio */
  label?: ReactNode
  /** Name used for html input element */
  name: string
  /** The Radio change handler. Fires when user change state of Radio */
  onChange?: (e: React.SyntheticEvent) => void
  /** Value used for html input element */
  value?: string
  /** Use larger touch area */
  hasTouchArea?: boolean
  /** Label size */
  labelSize?: Extract<TextVariant, 'bodyM' | 'bodyS'>
}

const RADIO_SIZE = 20
const RADIO_DOT_SIZE = 7
const LABEL_MARGIN_LEFT = 10
const TOUCHAREA_SIZE_LG = 12

const RadioElement = styled.div`
  display: flex;
`

export const RadioContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-left: ${LABEL_MARGIN_LEFT}px;
  color: ${cssTheme.colors.inputRadio.unselected.default.content};
`

export const RadioIcon = styled.div`
  position: relative;
  width: ${RADIO_SIZE}px;
  min-width: ${RADIO_SIZE}px;
  height: ${RADIO_SIZE}px;
  background-color: ${cssTheme.colors.inputRadio.unselected.default.background};
  border: 1px solid ${cssTheme.colors.inputRadio.unselected.default.border};
  border-radius: ${cssTheme.layout.radius.rounded};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    transform 0.15s ease-in-out,
    background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out;

  &:after {
    content: '';
    width: ${RADIO_DOT_SIZE}px;
    height: ${RADIO_DOT_SIZE}px;
    background-color: ${cssTheme.colors.inputRadio.unselected.default
      .background};
    border-radius: ${cssTheme.layout.radius.rounded};
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) scale(0);
  }
`

export const RadioInput = styled.input`
  display: block;
  position: absolute;
  top: 0;
  left: ${RADIO_SIZE + TOUCHAREA_SIZE_LG}px;
  opacity: 0;
  width: 0;
  height: 0;

  &:focus-visible + ${RadioIcon} {
    outline: 2px solid ${cssTheme.colors.border.interactive.primaryActive};
    outline-offset: 2px;
  }

  &:checked + ${RadioIcon} {
    background-color: ${cssTheme.colors.inputRadio.selected.default.background};
    border-color: ${cssTheme.colors.inputRadio.selected.default.border};
    &:after {
      transform: translate(-50%, -50%) scale(1);
    }
  }

  &[data-field-error='true'] + ${RadioIcon} {
    background-color: ${cssTheme.colors.inputRadio.error.default.background};
    border-color: ${cssTheme.colors.inputRadio.error.default.border};

    &:after {
      background-color: ${cssTheme.colors.inputRadio.error.default.border};
    }
  }

  &:disabled + ${RadioIcon} {
    background-color: ${cssTheme.colors.inputRadio.disabled.background};
    border-color: ${cssTheme.colors.inputRadio.disabled.border};
    cursor: not-allowed;

    &:after {
      background-color: ${cssTheme.colors.inputRadio.disabled.icon};
    }
  }
  &:disabled ~ ${RadioContent} {
    cursor: not-allowed;
    color: ${cssTheme.colors.inputRadio.disabled.content};
  }
`

const getRadioBodyMargin = (
  hasTouchArea?: boolean,
  labelSize?: TextVariant
) => {
  if (!hasTouchArea) return `0`

  if (hasTouchArea && labelSize === 'bodyM')
    return `${cssTheme.layout.spacing.s300} 0`

  if (hasTouchArea && labelSize === 'bodyS')
    return `${cssTheme.layout.spacing.s400} 0 0`

  return `${cssTheme.layout.spacing.s100}`
}

export const RadioBody = styled.label<
  Pick<Props, 'hasTouchArea' | 'fullWidth' | 'labelSize'>
>`
  display: flex;
  position: relative;
  margin: ${({hasTouchArea, labelSize}) =>
    getRadioBodyMargin(hasTouchArea, labelSize)};

  ${({fullWidth}) => (fullWidth ? 'flex: 1;' : '')}

  ${linariaMq.desktopXs} {
    &:hover {
      ${RadioInput}:not(:disabled):not(:active) {
        & + ${RadioIcon} {
          border-color: ${cssTheme.colors.inputRadio.unselected.hover.border};
        }

        &:checked + ${RadioIcon} {
          background-color: ${cssTheme.colors.inputRadio.selected.hover
            .background};
          border-color: ${cssTheme.colors.inputRadio.selected.hover.border};
        }

        &[data-field-error='true'] + ${RadioIcon} {
          background-color: ${cssTheme.colors.inputRadio.error.hover
            .background};
          border-color: ${cssTheme.colors.inputRadio.error.hover.border};
          &:after {
            background-color: ${cssTheme.colors.inputRadio.error.hover.border};
          }
        }
      }
    }
  }

  @supports selector(:has(input:disabled)) {
    cursor: pointer;
    :has(input:disabled) {
      cursor: not-allowed;
    }
  }
`

export const StyledText = styled(Text)`
  line-height: ${RADIO_SIZE}px !important;
`

export const Radio = ({
  checked,
  value,
  name,
  dataId,
  onChange,
  label,
  disabled,
  fullWidth = false,
  hasError = false,
  hasTouchArea = false,
  className,
  labelSize = 'bodyS'
}: Props) => (
  <RadioElement className={className}>
    <RadioBody
      hasTouchArea={hasTouchArea}
      fullWidth={fullWidth}
      labelSize={labelSize}
    >
      <RadioInput
        type="radio"
        name={name}
        data-id={dataId}
        checked={checked}
        value={value}
        disabled={disabled}
        data-field-error={hasError}
        onChange={onChange}
      />
      <RadioIcon />
      {Boolean(label) && (
        <RadioContent>
          <StyledText variant={labelSize} tag="span">
            {label}
          </StyledText>
        </RadioContent>
      )}
    </RadioBody>
  </RadioElement>
)
